'use client';

import styled from '@emotion/styled';
import { Flex, Grid, Link, LinkProps, Text, useTokens } from '@mezzoforte/forge';
import { LockSimple } from '@mezzoforte/forge-icons';
import { Fragment, useState } from 'react';
import InfoTooltip from '@/components/InfoTooltip/InfoTooltip';
import { NextLinkButton } from '@/components/Link/LinkButton';
import { useContactStats } from '@/hooks/useEntryStats';
import { useSession } from '@/hooks/useSession';
import { formatNumber } from '@/util/number';
import { AdminTools } from '@/components/QuickEdit/AdminTools';

const { colors, space } = useTokens.huutokaupat;

const Fieldset = styled('fieldset')`
  border: 1px solid ${colors.backgroundGray};
  padding: ${space[3]};
  margin-top: ${space[2]};
`;

interface QuickEditToolsProps {
  entryId: string;
  viewCount: number;
  favoriteCount: number;
  companyId: number;
  categoryId: number;
  isAdmin: boolean;
}

function QuickEditTools({ entryId, viewCount, favoriteCount, categoryId, companyId, isAdmin }: QuickEditToolsProps) {
  const [callsModalOpen, setCallsModalOpen] = useState(false);
  const stats = useContactStats(entryId);

  const statistics: ({ title: string; tooltip: string; count: number } & Partial<LinkProps>)[] = [
    {
      title: 'Katsottu',
      tooltip:
        'Kertoo kuinka moni käyttäjä on avannut kohdesivun, eli käynyt katsomassa kohdetta. Kävijä on voinut tulla Huutokaupat.com-sivulta tai jostain muualta, kuten Facebookista tai Google-mainoksen kautta.',
      count: viewCount,
    },
    {
      title: 'Seuraajia',
      tooltip:
        'Kertoo kuinka moni käyttäjä on lisännyt kohteen omalle seurantalistalleen, eli on todella kiinnostunut kohteesta.',
      count: favoriteCount,
    },
    {
      title: 'Soitot',
      tooltip: 'Kertoo, kuinka monta kertaa yhteyshenkilön puhelinnumeroa on katsottu Soita-nappia painamalla.',
      count: stats?.call.length ?? 0,
      ...(isAdmin ? { onClick: () => setCallsModalOpen(true), role: 'button', tabIndex: 0 } : {}),
    },
    {
      title: 'Viestit',
      tooltip: 'Kertoo, kuinka monta viestiä on lähetetty yhteyshenkilölle kohdesivulta.',
      count: stats?.message.length ?? 0,
      ...(isAdmin ? { href: `/yllapito/yritykset/${companyId}/tiedustelut/${entryId}` } : {}),
    },
  ];

  return (
    <Grid
      templateColumns={{ base: '1fr', md: '1fr auto' }}
      gap={2}
    >
      <Flex
        gap={2}
        mb={{ base: 2, md: 0 }}
        wrap="wrap"
        height="fit-content"
        data-test="quick-edit-actions"
      >
        {isAdmin ? (
          <AdminTools
            entryId={entryId}
            categoryId={categoryId}
            calls={stats?.call ?? []}
            callsModalOpen={callsModalOpen}
            onCallsModalDismiss={() => setCallsModalOpen(false)}
          />
        ) : (
          <NextLinkButton href={`/hallinta/nettihuutokaupat/${entryId}`}>Muokkaa</NextLinkButton>
        )}
      </Flex>
      <Grid
        as="dl"
        templateColumns="85px auto auto"
        data-test="quick-edit-statistics"
      >
        {statistics.map(({ title, count, tooltip, ...stat }) => (
          <Fragment key={title}>
            <dt>
              {(stat.href ?? stat.onClick) ? (
                <Link
                  {...stat}
                  sx={{
                    fontSize: 14,
                    textAlign: 'left',
                    pr: 2,
                    fontWeight: 'bold',
                  }}
                >
                  {title}
                </Link>
              ) : (
                <Text
                  sx={{
                    fontSize: 14,
                    textAlign: 'left',
                    pr: 2,
                    fontWeight: 'bold',
                  }}
                >
                  {title}:
                </Text>
              )}
            </dt>
            <Text as="dd">{formatNumber(count)}</Text>
            <Text as="dd">
              <InfoTooltip label={tooltip} />
            </Text>
          </Fragment>
        ))}
      </Grid>
    </Grid>
  );
}

interface QuickEditProps {
  entryId: string;
  viewCount: number;
  isPrivateAuction: boolean;
  companyId: number;
  categoryId: number;
  isConsumerAuction: boolean;
}

export default function QuickEdit({
  entryId,
  viewCount,
  isPrivateAuction,
  companyId,
  categoryId,
  isConsumerAuction,
}: QuickEditProps) {
  const { currentUser, entryInfo } = useSession();
  const { data: userEntryInfo, isPending } = entryInfo(Number(entryId));
  const isAdmin = currentUser.data?.user?.isAdmin ?? false;

  if (
    isPending ||
    !userEntryInfo?.isLoggedIn ||
    (!userEntryInfo.isPublisher && !isAdmin) ||
    (isConsumerAuction && !isAdmin)
  ) {
    return null;
  }

  return (
    <Fieldset
      data-test="quick-edit"
      className="no-print"
    >
      <Text
        as="legend"
        fontSize={20}
      >
        {isAdmin ? 'Ylläpitäjän' : 'Ilmoittajan'} valikko
      </Text>
      {isPrivateAuction && (
        <Text mb={2}>
          <LockSimple size="16" /> Suljettu huutokauppa, näkyy vain osallistujille
        </Text>
      )}
      <QuickEditTools
        entryId={entryId}
        viewCount={viewCount}
        favoriteCount={userEntryInfo.favoriteCount}
        companyId={companyId}
        categoryId={categoryId}
        isAdmin={isAdmin}
      />
    </Fieldset>
  );
}
